import { Box, useMediaQuery } from '@mui/material';

import youtube from 'assets/footer/youtube.svg';
import linkedin from 'assets/footer/linkedin.svg';
import logo from 'assets/logo.svg';
import chevron from 'assets/common/icons/chevron-white.svg';

import {
  aboutMeta,
  applicationsMeta,
  automaticDatasetEnhancementApplicationMeta,
  careersMeta,
  homeMeta,
  intelligentDataPipelineApplicationMeta,
  intelligentSystemTestingApplicationMeta,
  platformMeta,
  policyMeta,
  reinforcementLearningApplicationMeta,
  resourcesMeta,
  sensorsMeta,
} from 'constants/pages';
import { externalLinks } from 'constants/externalLinks';

import {
  FooterNavigation,
  Link,
  SocialMediaBox,
  SubText,
  LinksWrapper,
  CopyrightInfo,
  LinkTitle,
  LogoAndNav,
  FooterWrapper,
  LinksSectionCompany,
  LinksSectionPlatform,
  LinksSectionIndustries,
  PrivacyLink,
  InfoLink,
  SubpageList,
  LinksSectionApplications,
  LinksSectionResources,
  Title,
} from './Footer.style';
import { theme } from 'styles';
import {
  navyDefenceData,
  industriesMeta,
  maritimeData,
  mobileData,
  aeronauticsData,
} from 'constants/industries/industries';

import { useState } from 'react';
import { Accordion } from 'components/Accordion';

export enum NavAccordionKey {
  Platform = 'platform',
  Industries = 'industries',
  Applications = 'applications',
  Customer_case = 'customer-case',
  Company = 'company',
  ActionPacks = 'action-packs',
}

export const Footer = () => {
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const [openKey, setOpenKey] = useState<NavAccordionKey | null>(null);

  const handleToggle = (key: NavAccordionKey) => {
    setOpenKey((prev) => (prev === key ? null : key));
  };

  return (
    <FooterWrapper component="footer">
      <FooterNavigation>
        <LogoAndNav>
          <Box className="logo-wrapper">
            <Link to={homeMeta.url}>
              <img src={logo} alt="logo icon" />
            </Link>
          </Box>

          <LinksWrapper>
            <LinksSectionPlatform>
              <Accordion
                trigger={
                  <Title isOpen={openKey === NavAccordionKey.Platform}>
                    Platform
                    {!isDesktop && <img src={chevron} alt="Chevron icon" />}
                  </Title>
                }
                isDesktop={isDesktop}
                isOpen={openKey === NavAccordionKey.Platform}
                onToggle={() => handleToggle(NavAccordionKey.Platform)}
              >
                <SubpageList>
                  <Link to={platformMeta.url}>Overview</Link>
                  <Link to={sensorsMeta.url}>Sensors</Link>
                </SubpageList>
              </Accordion>
            </LinksSectionPlatform>

            <LinksSectionIndustries>
              <Accordion
                trigger={
                  <Title isOpen={openKey === NavAccordionKey.Industries}>
                    Industries
                    {!isDesktop && <img src={chevron} alt="Chevron icon" />}
                  </Title>
                }
                isDesktop={isDesktop}
                isOpen={openKey === NavAccordionKey.Industries}
                onToggle={() => handleToggle(NavAccordionKey.Industries)}
              >
                <SubpageList>
                  <Link to={industriesMeta.url}>All Industries</Link>
                  <Link to={maritimeData.meta.url}>Maritime</Link>
                  <Link to={navyDefenceData.meta.url}>Navy & Defense Sector</Link>
                  <Link to={aeronauticsData.meta.url}>Aeronautics</Link>
                  <Link to={mobileData.meta.url}>Mobile Machines</Link>
                </SubpageList>
              </Accordion>
            </LinksSectionIndustries>

            <LinksSectionApplications>
              <Accordion
                trigger={
                  <Title isOpen={openKey === NavAccordionKey.Applications}>
                    Applications
                    {!isDesktop && <img src={chevron} alt="Chevron icon" />}
                  </Title>
                }
                isDesktop={isDesktop}
                isOpen={openKey === NavAccordionKey.Applications}
                onToggle={() => handleToggle(NavAccordionKey.Applications)}
              >
                <SubpageList>
                  <Link to={applicationsMeta.url}>All Applications</Link>
                  <Link to={intelligentSystemTestingApplicationMeta.url}>
                    {intelligentSystemTestingApplicationMeta.title}
                  </Link>
                  <Link to={intelligentDataPipelineApplicationMeta.url}>
                    {intelligentDataPipelineApplicationMeta.title}
                  </Link>
                  <Link to={automaticDatasetEnhancementApplicationMeta.url}>
                    {automaticDatasetEnhancementApplicationMeta.title}
                  </Link>
                  <Link to={reinforcementLearningApplicationMeta.url}>
                    {reinforcementLearningApplicationMeta.title}
                  </Link>
                </SubpageList>
              </Accordion>
            </LinksSectionApplications>

            <LinksSectionCompany>
              <Accordion
                trigger={
                  <Title isOpen={openKey === NavAccordionKey.Company}>
                    Company
                    {!isDesktop && <img src={chevron} alt="Chevron icon" />}
                  </Title>
                }
                isDesktop={isDesktop}
                isOpen={openKey === NavAccordionKey.Company}
                onToggle={() => handleToggle(NavAccordionKey.Company)}
              >
                <SubpageList>
                  <Link to={aboutMeta.url}>About us</Link>
                  <Link to={careersMeta.url}>Careers</Link>
                </SubpageList>
              </Accordion>
            </LinksSectionCompany>

            <LinksSectionResources>
              <LinkTitle>
                <Link to={resourcesMeta.url}>Resources</Link>
              </LinkTitle>
            </LinksSectionResources>
          </LinksWrapper>

          <Box
            display={{
              xxs: 'none',
              lg: 'flex',
            }}
            justifyContent="flex-end"
            marginBottom="36px"
          >
            <InfoLink href="mailto:info@ailivesim.com">info@ailivesim.com</InfoLink>
          </Box>
        </LogoAndNav>

        <Box
          display={{
            xxs: 'block',
            lg: 'none',
          }}
          textAlign="center"
          marginBottom="16px"
        >
          <InfoLink href="mailto:info@ailivesim.com">info@ailivesim.com</InfoLink>
        </Box>
        <CopyrightInfo>
          {!isDesktop && (
            <SocialMediaBox>
              <a
                href={externalLinks.youTubeChannel}
                style={{
                  marginRight: '16px',
                }}
              >
                <img alt="youtube icon" src={youtube} />
              </a>

              <a href={externalLinks.linkedin}>
                <img alt="linkedin icon" src={linkedin} />
              </a>
            </SocialMediaBox>
          )}

          <SubText sx={{ margin: { xxs: '16px 0', lg: 0 } }}>
            <Link to={policyMeta.url} style={{ margin: 0, fontSize: '12px', lineHeight: '15px' }}>
              <PrivacyLink>Privacy policy </PrivacyLink>
            </Link>
          </SubText>

          <SubText>
            © {new Date().getFullYear()}&nbsp;
            <Link
              style={{
                margin: 0,
                paddingLeft: '4px',
                fontSize: '12px',
                lineHeight: '15px',
                textDecoration: 'underline',
              }}
              to={homeMeta.url}
            >
              AILiveSim
            </Link>
            . All Rights Reserved
          </SubText>

          {isDesktop && (
            <SocialMediaBox>
              <a
                href={externalLinks.youTubeChannel}
                style={{
                  marginRight: '16px',
                }}
              >
                <img alt="youtube icon" src={youtube} />
              </a>

              <a href={externalLinks.linkedin}>
                <img alt="linkedin icon" src={linkedin} />
              </a>
            </SocialMediaBox>
          )}
        </CopyrightInfo>
      </FooterNavigation>
    </FooterWrapper>
  );
};
